// quiz_management/StudentDashboard.jsx
// Comprehensive student dashboard with analytics and progress tracking

import React, { useState, useEffect } from 'react';
import './quizstudentdashboard.css';
import API_BASE_URL from './config';
import { API_BASE, authFetch } from '../services/api.js';
import PerformanceReport from './performancereport';
import EnhancedQuizHistory from './quizenhancedhistory';
import ReviewMistakes from './quizreviewmistakes';

const StudentDashboard = ({ studentId, onQuizSelect }) => {
    const [dashboardData, setDashboardData] = useState(null);
    const [incorrectQuestions, setIncorrectQuestions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [activeTab, setActiveTab] = useState('overview'); // overview, history, incorrect, analytics

    
    useEffect(() => {
        loadDashboardData();
    }, [studentId]);



    const loadDashboardData = async () => {
        try {
            setLoading(true);
            
            // Load student report
            const reportResponse = await authFetch(`${API_BASE_URL}/quiz/report/student/${studentId}?student_id=${studentId}`);
            const reportData = await reportResponse.json();
            setDashboardData(reportData);
            
            // Load incorrect questions
            const incorrectResponse = await authFetch(`${API_BASE_URL}/quiz/report/student/${studentId}/incorrect-questions?student_id=${studentId}`);
            const incorrectData = await incorrectResponse.json();
            setIncorrectQuestions(incorrectData);
            
        } catch (err) {
            setError('Failed to load dashboard data. Please try again.');
            console.error('Dashboard loading error:', err);
        } finally {
            setLoading(false);
        }
    };

    
    const getPerformanceLevel = (percentage) => {
        if (percentage >= 80) return { level: 'Excellent', class: 'excellent' };
        if (percentage >= 60) return { level: 'Good', class: 'good' };
        if (percentage >= 40) return { level: 'Fair', class: 'fair' };
        return { level: 'Needs Improvement', class: 'poor' };
    };

    const formatTime = (minutes) => {
        if (minutes < 60) return `${minutes} min`;
        const hours = Math.floor(minutes / 60);
        const mins = minutes % 60;
        return `${hours}h ${mins}m`;
    };

    const groupIncorrectQuestionsByTopic = () => {
        const grouped = {};
        incorrectQuestions.forEach(q => {
            const key = 'General'; // You could enhance this with actual topic info
            if (!grouped[key]) grouped[key] = [];
            grouped[key].push(q);
        });
        return grouped;
    };

    if (loading) {
        return (
            <div className="dashboard-container">
                <div className="loading-spinner">Loading dashboard...</div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="dashboard-container">
                <div className="error-message">{error}</div>
                <button className="retry-btn" onClick={loadDashboardData}>
                    Retry
                </button>
            </div>
        );
    }

    const performanceData = getPerformanceLevel(dashboardData?.average_score || 0);

    return (
        <div className="dashboard-container">
            <div className="dashboard-header">
                <h1>Student Dashboard</h1>
                <p>Track your progress and improve your performance</p>
            </div>

            <div className="dashboard-tabs">
                <button 
                    className={`tab-btn ${activeTab === 'overview' ? 'active' : ''}`}
                    onClick={() => setActiveTab('overview')}
                >
                    Overview
                </button>
                <button 
                    className={`tab-btn ${activeTab === 'history' ? 'active' : ''}`}
                    onClick={() => setActiveTab('history')}
                >
                    Quiz History
                </button>
                <button 
                    className={`tab-btn ${activeTab === 'incorrect' ? 'active' : ''}`}
                    onClick={() => setActiveTab('incorrect')}
                >
                    Review Mistakes
                </button>
                <button 
                    className={`tab-btn ${activeTab === 'analytics' ? 'active' : ''}`}
                    onClick={() => setActiveTab('analytics')}
                >
                    Analytics
                </button>

                <button 
                    className={`tab-btn ${activeTab === 'performance' ? 'active' : ''}`}
                    onClick={() => setActiveTab('performance')}
                >
                    Performance Report
                </button>
            </div>

            {activeTab === 'overview' && (
                <div className="overview-section">
                    <div className="stats-grid">
                        <div className="stat-card primary">
                            <h3>{dashboardData?.total_attempts || 0}</h3>
                            <p>Total Attempts</p>
                            <div className="stat-icon">📝</div>
                        </div>
                        <div className="stat-card success">
                            <h3>{dashboardData?.completed_attempts || 0}</h3>
                            <p>Completed Quizzes</p>
                            <div className="stat-icon">✅</div>
                        </div>
                        <div className="stat-card info">
                            <h3>{dashboardData?.average_score?.toFixed(1) || 0}%</h3>
                            <p>Average Score</p>
                            <div className="stat-icon">📊</div>
                        </div>
                        <div className="stat-card warning">
                            <h3>{dashboardData?.best_score?.toFixed(1) || 0}%</h3>
                            <p>Best Score</p>
                            <div className="stat-icon">🏆</div>
                        </div>
                    </div>

                    <div className="performance-overview">
                        <div className="performance-card">
                            <h3>Performance Level</h3>
                            <div className={`performance-indicator ${performanceData.class}`}>
                                <span className="performance-level">{performanceData.level}</span>
                                <div className="performance-bar">
                                    <div 
                                        className="performance-fill"
                                        style={{ width: `${dashboardData?.average_score || 0}%` }}
                                    ></div>
                                </div>
                                <span className="performance-score">
                                    {dashboardData?.average_score?.toFixed(1) || 0}%
                                </span>
                            </div>
                        </div>

                        <div className="time-spent-card">
                            <h3>Study Time</h3>
                            <div className="time-display">
                                <span className="time-value">
                                    {formatTime(dashboardData?.total_time_spent || 0)}
                                </span>
                                <span className="time-label">Total Time Spent</span>
                            </div>
                        </div>
                    </div>

                    <div className="quick-actions">
                        <h3>Quick Actions</h3>
                        <div className="action-buttons">
                            <button 
                                className="action-btn primary"
                                onClick={() => setActiveTab('history')}
                            >
                                View Quiz History
                            </button>
                            <button 
                                className="action-btn secondary"
                                onClick={() => setActiveTab('incorrect')}
                            >
                                Review Mistakes ({incorrectQuestions.length})
                            </button>
                            <button 
                                className="action-btn success"
                                onClick={() => setActiveTab('analytics')}
                            >
                                View Analytics
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {activeTab === 'history' && (
                <EnhancedQuizHistory studentId={studentId} onQuizSelect={onQuizSelect} />
            )}

            {activeTab === 'incorrect' && (
                <ReviewMistakes studentId={studentId} />
            )}
            
            {activeTab === 'analytics' && (
                <div className="analytics-section">
                    <h3>Performance Analytics</h3>
                    
                    <div className="analytics-grid">
                        <div className="analytics-card">
                            <h4>Score Distribution</h4>
                            <div className="score-ranges">
                                <div className="score-range excellent">
                                    <span className="range-label">80-100%</span>
                                    <div className="range-bar">
                                        <div className="range-fill" style={{width: '40%'}}></div>
                                    </div>
                                    <span className="range-count">4 attempts</span>
                                </div>
                                <div className="score-range good">
                                    <span className="range-label">60-79%</span>
                                    <div className="range-bar">
                                        <div className="range-fill" style={{width: '30%'}}></div>
                                    </div>
                                    <span className="range-count">3 attempts</span>
                                </div>
                                <div className="score-range fair">
                                    <span className="range-label">40-59%</span>
                                    <div className="range-bar">
                                        <div className="range-fill" style={{width: '20%'}}></div>
                                    </div>
                                    <span className="range-count">2 attempts</span>
                                </div>
                                <div className="score-range poor">
                                    <span className="range-label">0-39%</span>
                                    <div className="range-bar">
                                        <div className="range-fill" style={{width: '10%'}}></div>
                                    </div>
                                    <span className="range-count">1 attempt</span>
                                </div>
                            </div>
                        </div>

                        <div className="analytics-card">
                            <h4>Improvement Trend</h4>
                            <div className="trend-indicator">
                                <div className="trend-arrow up">↗</div>
                                <div className="trend-text">
                                    <span className="trend-value">+12%</span>
                                    <span className="trend-label">improvement over last 5 attempts</span>
                                </div>
                            </div>
                        </div>

                        <div className="analytics-card">
                            <h4>Study Recommendations</h4>
                            <ul className="recommendations">
                                <li>Focus on topics where you scored below 60%</li>
                                <li>Review explanations for incorrect answers</li>
                                <li>Take practice quizzes regularly</li>
                                <li>Spend more time on difficult questions</li>
                            </ul>
                        </div>

                        <div className="analytics-card">
                            <h4>Achievement Progress</h4>
                            <div className="achievements">
                                <div className="achievement earned">
                                    <span className="achievement-icon">🏆</span>
                                    <span className="achievement-name">First Quiz Completed</span>
                                </div>
                                <div className="achievement earned">
                                    <span className="achievement-icon">📚</span>
                                    <span className="achievement-name">Study Streak: 5 days</span>
                                </div>
                                <div className="achievement pending">
                                    <span className="achievement-icon">⭐</span>
                                    <span className="achievement-name">Perfect Score (90%+ needed)</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* the other reports hav their code written in this file itself. but for performance report we made a separate file*/}
            {activeTab === 'performance' && (
                <PerformanceReport studentId={studentId} />
            )}
        </div>
    );
};

export default StudentDashboard;